import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import SubPageHero from "@/components/landing/SubPageHero";
import InvestorAccessModal from "@/components/landing/InvestorAccessModal";
import { LANDING } from "@/constants/testIds";

/**
 * /family-office-investment-solutions — dedicated institutional page.
 * One global service delivered through KR's existing regional network.
 * Follows the Capital Pathways / Strategies editorial structure.
 */
const STAGES = [
  {
    n: "01",
    title: "Define the Mandate",
    body: "Investment objectives, sectors, jurisdictions, transaction sizes, risk parameters, return requirements, governance preferences and exclusions are documented at the beginning of the relationship.",
  },
  {
    n: "02",
    title: "Screen & Structure",
    body: "Aligned opportunities are assessed for sponsor quality, commercial viability, financial performance, capital structure, downside protection, execution readiness and identifiable exit pathways.",
  },
  {
    n: "03",
    title: "Support Execution",
    body: "KR coordinates the required diligence workstreams, supports transaction structuring and negotiation, and prepares the materials required for an informed investment decision.",
  },
];

const CAPABILITIES = [
  {
    title: "Mandate Development",
    body: "Defining the family's investment criteria, priorities, restrictions and approval process.",
  },
  {
    title: "Opportunity Origination",
    body: "Identifying relevant private-market opportunities through KR's operating, advisory and capital relationships.",
  },
  {
    title: "Initial Screening",
    body: "Testing mandate alignment, sponsor credibility, commercial fundamentals and transaction readiness.",
  },
  {
    title: "Commercial & Financial Review",
    body: "Reviewing business performance, financial assumptions, capital requirements, return potential and downside scenarios.",
  },
  {
    title: "Diligence Coordination",
    body: "Coordinating legal, tax, technical, regulatory, insurance and specialist advisers where required.",
  },
  {
    title: "Transaction Structuring",
    body: "Developing equity, credit, hybrid, SPV or co-investment structures appropriate to the opportunity and jurisdiction.",
  },
  {
    title: "Investment Committee Support",
    body: "Preparing concise decision materials covering the thesis, structure, risks, mitigants, diligence findings and outstanding conditions.",
  },
  {
    title: "Execution & Oversight",
    body: "Supporting negotiation, closing coordination and agreed post-transaction reporting or monitoring.",
  },
];

const COVERAGE = [
  "Austin",
  "New York",
  "London",
  "Geneva",
  "Dubai",
  "Singapore",
  "Hong Kong",
];

const Eyebrow = ({ children }) => (
  <div className="flex items-center gap-3 mb-6">
    <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
      {children}
    </span>
    <span className="w-10 h-px bg-[var(--kr-gold)]/60" />
  </div>
);

const FamilyOfficePage = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const openModal = useCallback(() => setModalOpen(true), []);
  const closeModal = useCallback(() => setModalOpen(false), []);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Navbar onRequestAccess={openModal} />
      <main
        data-testid={LANDING.familyOfficePage}
        className="relative bg-[var(--kr-bg)]"
      >
        <SubPageHero
          index="Family Office Investment Solutions"
          label="Family Offices · Strategic Capital · Global"
          title={
            <>
              A disciplined{" "}
              <span className="italic text-[var(--kr-gold)]">gateway</span>{" "}
              to private-market opportunities.
            </>
          }
          description="Kellermann Reid & Co. supports select family offices and strategic capital partners seeking a disciplined approach to private-market origination, screening and transaction execution."
          breadcrumb="Family Office Investment Solutions"
        />

        {/* Introduction */}
        <section
          data-testid={LANDING.familyOfficeIntro}
          className="py-20 lg:py-28"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>Introduction</Eyebrow>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.8rem,3.2vw,2.6rem)] leading-[1.15]">
                An extension of the family&apos;s{" "}
                <span className="italic text-[var(--kr-gold)]">
                  investment process
                </span>
                .
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <p className="text-[var(--kr-text)] text-[16.5px] leading-[1.85] max-w-[66ch] mb-8">
                We work as an extension of the family&apos;s investment
                process — translating investment objectives into a defined
                mandate, filtering opportunities against documented criteria,
                coordinating diligence and structuring, and preparing
                decision-ready recommendations for investment committee
                consideration.
              </p>
              <p className="font-serif-display text-[var(--kr-text)] text-[1.35rem] lg:text-[1.5rem] leading-[1.5] border-l-2 border-[var(--kr-gold)] pl-8 max-w-[44ch]">
                The family retains full discretion over every investment
                decision.
              </p>
            </div>
          </div>
        </section>

        {/* Section 1 — Process */}
        <section
          data-testid={LANDING.familyOfficeProcess}
          className="py-20 lg:py-28 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12">
            <div className="grid lg:grid-cols-12 gap-12 mb-12">
              <div className="lg:col-span-6">
                <Eyebrow>Process</Eyebrow>
                <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[26ch]">
                  From opportunity flow to an{" "}
                  <span className="italic text-[var(--kr-gold)]">
                    investable
                  </span>{" "}
                  decision.
                </h2>
              </div>
              <div className="lg:col-span-6 lg:pl-8">
                <p className="text-[var(--kr-text-muted)] text-[15.5px] leading-[1.85] max-w-[58ch]">
                  Family offices are frequently presented with opportunities
                  that are incomplete, poorly structured or outside their
                  investment mandate. KR provides a structured process designed
                  to reduce that volume into a focused pipeline of relevant,
                  properly evaluated opportunities.
                </p>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-0 border-t border-l border-[var(--kr-line)]">
              {STAGES.map((s) => (
                <article
                  key={s.n}
                  data-testid={`${LANDING.familyOfficeStagePrefix}${s.n}`}
                  className="p-8 lg:p-10 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.03)] transition-colors duration-500"
                >
                  <div
                    className="w-12 h-12 border border-[var(--kr-gold)]/60 flex items-center justify-center font-mono-label text-[10px] text-[var(--kr-gold)] mb-6"
                    aria-hidden
                  >
                    {s.n}
                  </div>
                  <h3 className="font-serif-display text-[1.35rem] text-[var(--kr-text)] mb-4 leading-[1.25] group-hover:text-[var(--kr-gold)] transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-[var(--kr-text-muted)] text-[14px] leading-[1.8]">
                    {s.body}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Section 2 — Capabilities */}
        <section
          data-testid={LANDING.familyOfficeCapabilities}
          className="py-20 lg:py-28 border-t border-[var(--kr-line)]"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>Capabilities</Eyebrow>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[22ch]">
                A{" "}
                <span className="italic text-[var(--kr-gold)]">documented</span>{" "}
                investment process.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <dl className="grid sm:grid-cols-2 gap-0 border-t border-l border-[var(--kr-line)]">
                {CAPABILITIES.map((c, i) => (
                  <div
                    key={c.title}
                    data-testid={`${LANDING.familyOfficeCapabilityPrefix}${i}`}
                    className="p-7 lg:p-8 border-r border-b border-[var(--kr-line)] group hover:bg-[rgba(201,169,110,0.025)] transition-colors duration-500"
                  >
                    <dt className="flex items-start gap-3 mb-3">
                      <span
                        aria-hidden
                        className="mt-[9px] block w-[6px] h-[6px] flex-shrink-0"
                        style={{
                          background: "var(--kr-gold)",
                          transform: "rotate(45deg)",
                        }}
                      />
                      <span className="font-serif-display text-[1.2rem] leading-[1.25] text-[var(--kr-text)] group-hover:text-[var(--kr-gold)] transition-colors">
                        {c.title}
                      </span>
                    </dt>
                    <dd className="text-[var(--kr-text-muted)] text-[14px] leading-[1.8] pl-[18px]">
                      {c.body}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </section>

        {/* Section 3 — Global capability */}
        <section
          data-testid={LANDING.familyOfficeGlobal}
          className="py-20 lg:py-24 border-t border-[var(--kr-line)] bg-[var(--kr-bg-2)]"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>Global Coverage</Eyebrow>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[22ch]">
                Global capability.{" "}
                <span className="italic text-[var(--kr-gold)]">
                  Jurisdiction-specific
                </span>{" "}
                execution.
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <p className="text-[var(--kr-text)] text-[15.5px] leading-[1.85] max-w-[62ch] mb-10">
                Each mandate is delivered through KR&apos;s existing
                international network and adapted to the family&apos;s
                jurisdiction, governance requirements and investment
                objectives. Appropriate legal, tax, regulatory and specialist
                advisers are coordinated where required.
              </p>
              <div className="border-t border-[var(--kr-line-soft)] pt-6 flex flex-wrap items-baseline gap-x-6 gap-y-3">
                <span className="font-mono-label text-[10px] text-[var(--kr-text-muted)]">
                  Delivered through
                </span>
                <ul
                  data-testid={LANDING.familyOfficeCoverage}
                  className="flex flex-wrap gap-x-5 gap-y-2"
                >
                  {COVERAGE.map((c) => (
                    <li
                      key={c}
                      className="font-serif-display text-[1.05rem] text-[var(--kr-text)]"
                    >
                      {c}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/#offices"
                  data-testid={LANDING.familyOfficeFootprintLink}
                  className="kr-link font-mono-label text-[10px] ml-auto"
                >
                  Global Footprint →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4 — Control */}
        <section
          data-testid={LANDING.familyOfficeControl}
          className="py-20 lg:py-24 border-t border-[var(--kr-line)]"
        >
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-4">
              <Eyebrow>Governance</Eyebrow>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.3rem)] leading-[1.15] max-w-[22ch]">
                The family remains{" "}
                <span className="italic text-[var(--kr-gold)]">in control</span>
                .
              </h2>
            </div>
            <div className="lg:col-span-8 lg:pl-8">
              <p className="text-[var(--kr-text)] text-[15.5px] leading-[1.85] max-w-[62ch] mb-8">
                KR does not take custody of family assets or exercise
                discretionary authority under this service. The family office
                determines its mandate, approves each transaction and retains
                full investment discretion. Engagements are individually
                documented and remain subject to applicable legal, regulatory
                and jurisdictional requirements.
              </p>
              <p className="text-[13px] text-[var(--kr-text-muted)] italic leading-[1.75] max-w-[60ch]">
                Kellermann Reid &amp; Co. does not act as a placement agent or
                broker-dealer in any jurisdiction and does not guarantee
                funding or transaction outcomes. Nothing on this page
                constitutes an offer or solicitation.
              </p>
              <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3">
                <Link
                  to="/capital-pathways"
                  data-testid={LANDING.familyOfficePathwaysLink}
                  className="kr-link font-mono-label text-[11px]"
                >
                  Selective Capital Pathways →
                </Link>
                <Link
                  to="/disclosures"
                  data-testid={LANDING.familyOfficeDisclosuresLink}
                  className="kr-link font-mono-label text-[11px]"
                >
                  Disclosures →
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="border-t border-[var(--kr-line)] py-24 bg-[var(--kr-bg-2)]">
          <div className="max-w-[1480px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-4 mb-6">
                <span className="font-mono-label text-[10px] text-[var(--kr-gold)]">
                  Confidential
                </span>
                <span className="w-12 h-px bg-[var(--kr-gold)]/60" />
              </div>
              <h2 className="font-serif-display text-[var(--kr-text)] text-[clamp(1.7rem,2.9vw,2.4rem)] leading-[1.18] max-w-[30ch]">
                Discuss a Family Office{" "}
                <span className="italic text-[var(--kr-gold)]">Mandate</span>.
              </h2>
              <p className="mt-5 text-[var(--kr-text-muted)] text-[15px] leading-[1.85] max-w-[60ch]">
                For a confidential introductory discussion, provide a brief
                overview of your investment objectives, preferred sectors,
                geographic focus and typical transaction size.
              </p>
            </div>
            <div className="lg:col-span-5 flex flex-wrap gap-4 lg:justify-end">
              <button
                type="button"
                onClick={openModal}
                data-testid={LANDING.familyOfficeCta}
                className="kr-btn-primary"
              >
                Initiate a Confidential Discussion
                <span aria-hidden>→</span>
              </button>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <InvestorAccessModal
        open={modalOpen}
        onClose={closeModal}
        defaultInterest="family_office"
      />
    </>
  );
};

export default FamilyOfficePage;
